if (!customElements.get('subscription-popup')) {
  customElements.define(
    'subscription-popup',
    class SubscriptionPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat(['data-trigger-open', 'data-repeat-open']);
      }

      constructor() {
        super();
      }

      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        this.triggerOpen = this.dataset.triggerOpen;
        this.repeatOpen = this.dataset.repeatOpen;
        this.cookieName = 'sleektheme:popup';
        this.newsletterForm = this.querySelector('form[is="newsletter-form"]');

        window.FoxTheme.DOMready(this.handleOpen.bind(this));

        if (this.newsletterForm && this.querySelector('[data-newsletter-success]')) {
          this.newsletterForm.addEventListener('submit', this.handleNewsletterSubmit.bind(this));
        }

        window.FoxTheme.utils.addEventDelegate({
          context: this,
          selector: '.btn-apply-coupon-code',
          handler: async (e, target) => {
            e.preventDefault();
            const code = target.dataset.code;
            if (code) this.copyToClipboard(code, target);
          },
        });
      }

      prepareToShow() {
        super.prepareToShow();
        this.setRepeatOpen();
      }

      handleOpen() {
        if (Shopify.designMode) return;

        if (
          this.querySelector('[data-newsletter-success]:not([hidden])') ||
          this.querySelector('[data-newsletter-error]:not(.hidden)')
        ) {
          this.show();
          return;
        }

        let savedData = FoxTheme.utils.getStorage(this.cookieName);
        if (savedData && savedData.opened) {
          return false;
        }

        switch (this.triggerOpen) {
          case 'delay':
            setTimeout(() => {
              this.show();
            }, 5000);
            break;
          case 'scroll_down':
            this.triggerAfterScroll(700, this.show.bind(this));
            break;
          default:
            this.show();
            break;
        }
      }

      triggerAfterScroll(offset = 500, trigger) {
        let opened = false;
        const start = 0;
        window.addEventListener('scroll', (e) => {
          let scrollTop =
            window.pageYOffset || (document.documentElement || document.body.parentNode || document.body).scrollTop;

          if (scrollTop - start > offset && !opened) {
            trigger();
            opened = true;
            return;
          }
        });
      }

      copyToClipboard(code, button) {
        const originalText = button.textContent;
        const copyPromise = navigator.clipboard?.writeText
          ? navigator.clipboard.writeText(code)
          : Promise.reject(new Error('Clipboard API unavailable'));

        copyPromise
          .catch(() => {
            const codeInput = this.querySelector('[data-discount-code]');
            if (!codeInput) return;

            codeInput.select();
            codeInput.setSelectionRange(0, codeInput.value.length);
            document.execCommand('copy');
          })
          .finally(() => {
            button.textContent = button.dataset.copiedText;
            setTimeout(() => {
              button.textContent = originalText;
            }, 2000);
          });
      }

      async handleNewsletterSubmit(event) {
        event.preventDefault();

        if (!this.newsletterForm.reportValidity() || this.newsletterForm.dataset.submitting === 'true') return;

        const submitButton = this.newsletterForm.querySelector('[type="submit"]');
        const errorMessage = this.newsletterForm.querySelector('[data-newsletter-error]');
        const formData = new FormData(this.newsletterForm);

        this.newsletterForm.dataset.submitting = 'true';
        submitButton?.setAttribute('disabled', 'disabled');
        submitButton?.setAttribute('aria-busy', 'true');
        errorMessage?.classList.add('hidden');

        try {
          const response = await fetch(this.newsletterForm.action, {
            method: 'POST',
            body: formData,
            headers: { Accept: 'text/html' },
            credentials: 'same-origin',
          });
          const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
          const returnedForm = responseDocument.getElementById(this.newsletterForm.id);
          const subscribed =
            response.url.includes('customer_posted=true') ||
            Boolean(returnedForm?.querySelector('[id*="Newsletter-success"]'));

          if (subscribed) {
            this.showNewsletterSuccess();
            return;
          }

          const alreadySubscribed = response.url.includes('form_type=customer');
          const returnedError = returnedForm?.querySelector('.form-message--main');
          this.showNewsletterError(
            alreadySubscribed
              ? errorMessage?.dataset.alreadySubscribed
              : returnedError?.textContent.trim() || errorMessage?.dataset.genericError
          );
        } catch (error) {
          HTMLFormElement.prototype.submit.call(this.newsletterForm);
        } finally {
          delete this.newsletterForm.dataset.submitting;
          submitButton?.removeAttribute('disabled');
          submitButton?.removeAttribute('aria-busy');
        }
      }

      showNewsletterSuccess() {
        const fields = this.newsletterForm.querySelector('.newsletter-form__field-wrapper');
        const errorMessage = this.newsletterForm.querySelector('[data-newsletter-error]');
        const successMessage = this.newsletterForm.querySelector('[data-newsletter-success]');

        fields?.setAttribute('hidden', 'hidden');
        errorMessage?.classList.add('hidden');
        successMessage?.removeAttribute('hidden');
        successMessage?.querySelector('[tabindex="-1"]')?.focus();
      }

      showNewsletterError(message) {
        const errorMessage = this.newsletterForm.querySelector('[data-newsletter-error]');
        if (!errorMessage) return;

        errorMessage.textContent = message;
        errorMessage.classList.remove('hidden');
      }

      setRepeatOpen() {
        if (Shopify.designMode) return;

        let expires;
        switch (this.repeatOpen) {
          case 'no_repeat':
            expires = 365;
            break;
          case 'every_3_mins':
            expires = 3 / 60 / 24;
            break;
          case 'every_5_mins':
            expires = 5 / 60 / 24;
            break;
          case 'every_10_mins':
            expires = 1 / 6 / 24;
            break;
          case 'every_15_mins':
            expires = 15 / 60 / 24;
            break;
          case 'every_30_mins':
            expires = 1 / 2 / 24;
            break;
          case 'every_1_hr':
            expires = 1 / 24;
            break;
          case 'every_6_hrs':
            expires = 6 / 24;
            break;
          case 'every_12_hrs':
            expires = 1 / 2;
            break;
          case 'every_day':
            expires = 1;
            break;
          case 'every_3_days':
            expires = 3;
            break;
          case 'every_week':
            expires = 7;
            break;
          case 'every_2_weeks':
            expires = 14;
            break;
          case 'every_month':
            expires = 30;
            break;
          default:
            expires = 7;
            break;
        }

        window.FoxTheme.utils.setStorage(this.cookieName, { opened: true }, expires);
      }
    }
  );
}
